export interface Transformation {
  id: string
  title: string
  category: string
  description: string
  challenges: string[]
  solutions: string[]
  features: string[]
}

export interface TransformationsTranslations {
  title: string
  subtitle: string
  before: string
  after: string
  challenges: string
  solutions: string
  transformations: {
    [key: string]: Transformation
  }
}

export const transformationsTranslations: Record<string, TransformationsTranslations> = {
  en: {
    title: "My Transformations",
    subtitle: "See how I've reclaimed and reset overgrown gardens, giving them a fresh start and new potential.",
    before: "Before",
    after: "After",
    challenges: "Challenges",
    solutions: "Solutions",
    transformations: {
      'modern-garden': {
        id: 'modern-garden',
        title: 'Garden Clearance & Restoration',
        category: 'From Wild to Wonderful',
        description: 'Complete transformation of a garden with extensive vegetation growth into a clean, usable outdoor space. This project involved comprehensive clearance of overgrown vegetation and restoration to a tidy, functional condition.',
        challenges: [
          'Garden requiring extensive clearance and restoration',
          'Vegetation that had grown beyond manageable levels',
          'Limited access due to dense growth',
          'Safety concerns from overgrown areas'
        ],
        solutions: [
          'Initial assessment and safety evaluation',
          'Systematic clearance of overgrown vegetation',
          'Careful removal of thorny plants and weeds',
          'Disposal of garden waste and debris',
          'Final tidying and restoration of usable space'
        ],
        features: [
          'Completely cleared and restored garden space',
          'Safe and accessible outdoor area',
          'Ready for new planting and landscaping',
          'Significant improvement in property value',
          'Client able to enjoy their garden again'
        ]
      },
      'family-garden': {
        id: 'family-garden',
        title: 'Overgrown Garden Clearance & Discovery',
        category: 'The Lost Path Revealed',
        description: 'This garden featured extensive vegetation growth, with thorns, weeds, and dense overgrowth creating a challenging environment—making it difficult to see what lay beneath. After a comprehensive cut-back and thorough clearance, a hidden path and patio area were revealed. The transformation not only restored access but also uncovered a solid foundation for future use or a fresh new garden design.',
        challenges: [
          'Garden requiring comprehensive clearance and restoration',
          'Dense vegetation blocking access and visibility',
          'Hidden features and potential safety concerns',
          'Limited usable space due to overgrowth'
        ],
        solutions: [
          'Careful assessment to identify safe entry points',
          'Systematic cut-back and removal of dense vegetation',
          'Thorough clearance of thorns, brambles, and weeds',
          'Discovery and restoration of hidden path and patio area',
          'Preparation of the space for future use or redesign'
        ],
        features: [
          'Restored access to previously unusable garden',
          'Hidden path and patio uncovered',
          'Safe, open space ready for new planting or landscaping',
          'Solid foundation for future garden design',
          'Dramatic before-and-after transformation'
        ]
      },
      'urban-oasis': {
        id: 'urban-oasis',
        title: 'Lawn Recovery & First Cut',
        category: 'The Lawn Revival',
        description: 'The lawn had experienced extended growth periods, allowing the grass to reach significant heights. This job involved cutting it back to a manageable level and beginning the process of restoring the lawn to a healthy, tidy state. With regular care and attention moving forward, the garden is now well on its way back to its former green and lush condition.',
        challenges: [
          'Lawn requiring significant recovery and restoration',
          'Grass growth beyond normal maintenance levels',
          'Potential safety concerns from overgrown areas',
          'Lawn health requiring attention and care'
        ],
        solutions: [
          'Assessment of lawn condition and grass height',
          'Initial heavy cutting to remove overgrown grass',
          'Removal of grass clippings and debris',
          'Inspection of lawn health and soil condition',
          'Application of lawn care treatments if needed',
          'Establishment of maintenance schedule for ongoing care'
        ],
        features: [
          'Successfully cut back severely overgrown lawn',
          'Restored lawn to manageable and tidy appearance',
          'Improved lawn health and growth potential',
          'Established foundation for ongoing lawn maintenance',
          'Enhanced overall garden appearance and curb appeal'
        ]
      }
    }
  },
  cy: {
    title: "Fy Nhrausnewidiadau",
    subtitle: "Gweld sut rydw i wedi ailadrodd a hail-osod arddi wedi'u gor-dyfu, gan roi cychwyn ffres a photensial newydd iddynt.",
    before: "Cyn",
    after: "Ar ôl",
    challenges: "Heriau",
    solutions: "Atebion",
    transformations: {
      'modern-garden': {
        id: 'modern-garden',
        title: 'Clirio a Adfer Gardd',
        category: 'O Wyllt i Wych',
        description: 'Trawsnewid cyflawn o ardd gyda thyfiant helaeth i le awyr agored glân a defnyddiadwy. Roedd y prosiect hwn yn cynnwys clirio cynhwysfawr o dyfiant wedi\'i gor-dyfu ac adfer i gyflwr taclus a gweithredol.',
        challenges: [
          'Gardin sy\'n gofyn am glirio a adfer helaeth',
          'Tyfiant sydd wedi tyfu y tu hwnt i lefelau y gellir eu rheoli',
          'Mynediad cyfyngedig oherwydd tyfiant dwys',
          'Pryderon diogelwch o ardaloedd wedi\'u gor-dyfu'
        ],
        solutions: [
          'Asesiad cychwynnol a gwerthuso diogelwch',
          'Clirio systematig o dyfiant wedi\'i gor-dyfu',
          'Tynnu gofalus o blanhigion pigog a chwyn',
          'Gwaredu gwastraff gardd a malurion',
          'Tacluso terfynol ac adfer lle defnyddiadwy'
        ],
        features: [
          'Lle gardd wedi\'i glirio a\'i adfer yn llwyr',
          'Ardal awyr agored ddiogel a hygyrch',
          'Yn barod ar gyfer plannu a thirlunio newydd',
          'Gwelliant sylweddol yng ngwerth eiddo',
          'Cleient yn gallu mwynhau eu gardd eto'
        ]
      },
      'family-garden': {
        id: 'family-garden',
        title: 'Clirio a Darganfod Gardd Wedi\'i Gor-dyfu',
        category: 'Y Llwybr Colledig Wedi\'i Ddatgelu',
        description: 'Roedd y gardd hon yn cynnwys tyfiant helaeth, gyda phigau, chwyn, a thyfiant dwys yn creu amgylchedd heriol - gan ei gwneud yn anodd gweld beth oedd oddi tano. Ar ôl torri yn ôl cynhwysfawr a chlirio trylwyr, datgelwyd llwybr cudd ac ardal patio. Nid yn unig y gwnaeth y trawsnewid adfer mynediad ond hefyd ddatgelu sylfaen gadarn ar gyfer defnydd yn y dyfodol neu ddylunio gardd newydd ffres.',
        challenges: [
          'Gardin sy\'n gofyn am glirio a adfer cynhwysfawr',
          'Tyfiant dwys yn rhwystro mynediad a gwelededd',
          'Nodweddion cudd a phryderon diogelwch posibl',
          'Lle defnyddiadwy cyfyngedig oherwydd gor-dyfiant'
        ],
        solutions: [
          'Asesiad gofalus i nodi pwyntiau mynediad diogel',
          'Torri yn ôl systematig a thynnu tyfiant dwys',
          'Clirio trylwyr o bigau, mieri, a chwyn',
          'Darganfod ac adfer llwybr cudd ac ardal patio',
          'Paratoi\'r lle ar gyfer defnydd yn y dyfodol neu ail-ddylunio'
        ],
        features: [
          'Mynediad wedi\'i adfer i ardd a oedd yn anghyfaddas o\'r blaen',
          'Llwybr cudd a patio wedi\'u datgelu',
          'Lle agored, diogel yn barod ar gyfer plannu neu thirlunio newydd',
          'Sylfaen gadarn ar gyfer dylunio gardd yn y dyfodol',
          'Trawsnewid dramatig cyn-ac-ar-ôl'
        ]
      },
      'urban-oasis': {
        id: 'urban-oasis',
        title: 'Adfer Lawnt a\'r Torri Cyntaf',
        category: 'Adfywiad y Lawnt',
        description: 'Roedd y lawnt wedi profi cyfnodau tyfiant estynedig, gan ganiatáu i\'r glaswellt gyrraedd uchderau sylweddol. Roedd y swydd hon yn cynnwys ei dorri yn ôl i lefel y gellir ei reoli a dechrau\'r broses o adfer y lawnt i cyflwr iach a thaclus. Gyda gofal a sylw rheolaidd yn symud ymlaen, mae\'r gardd bellach yn dda ar ei ffordd yn ôl i\'w cyflwr gwyrdd a chyfoethog gynt.',
        challenges: [
          'Lawnt sy\'n gofyn am adfer a chynnal a chadw sylweddol',
          'Tyfiant glaswellt y tu hwnt i lefelau cynnal a chadw arferol',
          'Pryderon diogelwch posibl o ardaloedd wedi\'u gor-dyfu',
          'Iechyd lawnt sy\'n gofyn am sylw a gofal'
        ],
        solutions: [
          'Asesiad cyflwr lawnt a uchder glaswellt',
          'Torri trwm cychwynnol i dynnu glaswellt wedi\'i gor-dyfu',
          'Tynnu torion glaswellt a malurion',
          'Archwilio iechyd lawnt a chyflwr pridd',
          'Cymhwyso triniaethau gofal lawnt os oes angen',
          'Sefydlu amserlen cynnal a chadw ar gyfer gofal parhaus'
        ],
        features: [
          'Torri yn ôl yn llwyddiannus lawnt wedi\'i gor-dyfu yn ddifrifol',
          'Adfer lawnt i ymddangosiad y gellir ei reoli a thaclus',
          'Gwellhau iechyd lawnt a photensial tyfiant',
          'Sefydlu sylfaen ar gyfer cynnal a chadw lawnt parhaus',
          'Gwella ymddangosiad gardd cyffredinol a gwerth curb'
        ]
      }
    }
  }
} 